import cleanRecipient from '@/taskpane/common/clean-recipient'
import cleanRecipientList from '@/taskpane/common/clean-recipient-list'
import { officeIsComposeMode } from './office-mail-mode'

import {
  officeApiEvent_EndGetAsync,
  officeApiEvent_LocationGetAsync,
  officeApiEvent_OptionalAttendeesGetAsync,
  officeApiEvent_OrganizerGetAsync,
  officeApiEvent_RequiredAttendeesGetAsync,
  officeApiEvent_StartGetAsync,
} from './async/office-api-event-async'
import { officeApiItem_SharedPropertiesLoadAsync } from './async/office-api-mailbox-item-async'
import { officeDisplayName, officeUsername } from './office-user-profile'
import TimeInfo from '@/taskpane/events/models/time-info'
import getTimeInfo from '@/taskpane/common/get-time-info'
import uniquePeople from '../common/unique-people'

export async function officeEventStart(): Promise<Date> {
  if (officeIsComposeMode()) {
    return officeApiEvent_StartGetAsync()
  } else {
    return Office.context.mailbox.item.start
  }
}

export async function officeEventEnd(): Promise<Date> {
  if (officeIsComposeMode()) {
    return officeApiEvent_EndGetAsync()
  } else {
    return Office.context.mailbox.item.end
  }
}

export async function officeEventTime(isAllDay: boolean = false): Promise<TimeInfo> {
  const [start, end] = await Promise.all([officeEventStart(), officeEventEnd()])
  if (isAllDay && end) {
    end.setDate(end.getDate() - 1)
  }
  return getTimeInfo(start || new Date(), end || new Date())
}

export async function officeEventLocation(): Promise<string> {
  if (officeIsComposeMode()) {
    return officeApiEvent_LocationGetAsync()
  } else {
    return Office.context.mailbox.item.location || ''
  }
}

export async function officeEventOwner(): Promise<string> {
  const props = await officeApiItem_SharedPropertiesLoadAsync()
  if (props) {
    return props?.owner ? props.owner.toLowerCase() : ''
  } else {
    return ''
  }
}

export async function officeEventOrganizer(): Promise<Office.EmailAddressDetails> {
  let organizerDetails: Office.EmailAddressDetails

  if (officeIsComposeMode()) {
    organizerDetails = await officeApiEvent_OrganizerGetAsync()
    if (!organizerDetails) {
      organizerDetails = {
        displayName: officeDisplayName(),
        emailAddress: officeUsername(),
      } as Office.EmailAddressDetails
    }
  } else {
    organizerDetails = Office.context.mailbox.item.organizer || ({} as Office.EmailAddressDetails)
  }

  organizerDetails = cleanRecipient(organizerDetails)
  if (!organizerDetails.emailAddress && organizerDetails.displayName) {
    if (organizerDetails.displayName === officeDisplayName()) {
      organizerDetails.emailAddress = officeUsername()
    } else {
      organizerDetails.emailAddress = officeDisplayName()
    }
  }

  return organizerDetails
}

export async function officeEventAttendeesRequired(): Promise<Office.EmailAddressDetails[]> {
  let recipients: Office.EmailAddressDetails[]

  if (officeIsComposeMode()) {
    recipients = await officeApiEvent_RequiredAttendeesGetAsync()
  } else {
    recipients = Office.context.mailbox.item.requiredAttendees || []
  }

  return cleanRecipientList(recipients)
}

export async function officeEventAttendeesOptional(): Promise<Office.EmailAddressDetails[]> {
  let recipients: Office.EmailAddressDetails[]

  if (officeIsComposeMode()) {
    recipients = await officeApiEvent_OptionalAttendeesGetAsync()
  } else {
    recipients = Office.context.mailbox.item.optionalAttendees || []
  }

  return cleanRecipientList(recipients)
}

export async function officeEventAttendees(): Promise<Office.EmailAddressDetails[]> {
  const [organizer, requiredAttendees, optionalAttendees] = await Promise.all([
    officeEventOrganizer(),
    officeEventAttendeesRequired(),
    officeEventAttendeesOptional(),
  ])

  const attendees = [organizer].concat(requiredAttendees, optionalAttendees)
  return uniquePeople(attendees)
}

export async function officeEventAttendeesEmailAddresses(): Promise<string[]> {
  return (await officeEventAttendees()).map((recipient) => recipient.emailAddress)
}

const exportFunctions = {
  start: officeEventStart,
  end: officeEventEnd,
  time: officeEventTime,
  location: officeEventLocation,
  attendees: officeEventAttendees,
  organizer: officeEventOrganizer,
  requiredAttendees: officeEventAttendeesRequired,
  optionalAttendees: officeEventAttendeesOptional,
  emailAddresses: officeEventAttendeesEmailAddresses,
  owner: officeEventOwner,
}

export default exportFunctions
